import "./App.scss";

import React, { lazy, Suspense } from "react";
import cookies from "react-cookies";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useAppSelector } from "./app/hooks";
import { useGetCurrentUserQuery } from "./app/user/api";
import EmptyBox from "./components/EmptyBox";
import Fallback from "./components/Fallback";
import { COOKIES } from "./helpers/constants";
import PATH from "./helpers/PATH";
import AppLayout from "./layouts/App";
import AppBodyLayout from "./layouts/AppBody";
import AuthLayout from "./layouts/Auth";
import PageLayout from "./layouts/Page";
import PrivacyLayout from "./layouts/Privacy";
import BookADemo from "./views/BookADemo";
import CephalometricAnalysis from "./views/CephalometricAnalysis";
import ForgotPassword from "./views/ForgotPassword";
import SignIn from "./views/SignIn";
import SignUp from "./views/SignUp";
import Welcome from "./views/Welcome";

const Cases = lazy(() => import("./views/Cases"));
const Case = lazy(() => import("./views/Case"));
const Tariffs = lazy(() => import("./views/Tariffs"));
const Profile = lazy(() => import("./views/Profile/index"));
const Privacy = lazy(() => import("./views/Privacy"));
const Results = lazy(() => import("./views/Results"));
const ResultsNew = lazy(() => import("./views/ResultsNew"));
const PanoramicView = lazy(() => import("./views/PanoramicView"));
const PanoramicResultsView = lazy(() => import("./views/PanoramicResultsView"));
const ToothTestPage = lazy(() => import("./views/ToothTestPage"));
const SimpleTestPage = lazy(() => import("./views/SimpleTestPage"));
const PanoramicReport = lazy(() => import("./views/PanoramicReport"));
const SpecialtySelection = lazy(() => import("./views/SpecialtySelection"));
const CBCTViewer = lazy(() => import("./views/CBCTViewer"));
const GLTFViewer = lazy(() => import("./views/GLTFViewer"));
const Dictaphone = lazy(() => import("./views/Dictaphone"));
const DictaphoneProcessing = lazy(() => import("./views/DictaphoneProcessing"));
const DictaphoneResults = lazy(() => import("./views/DictaphoneResults"));

const AppAuthRoutes = () => {
  const token = useAppSelector((state) => state.userSlice.token);
  const location = useLocation();
  useGetCurrentUserQuery(null, { skip: !token });

  if (
    !token &&
    location.pathname !== PATH.HOME &&
    location.pathname !== PATH.CEPHALOMETRIC_ANALYSIS
  ) {
    if (
      !cookies.load(COOKIES.REDIRECT_URL) &&
      !location.pathname.includes(PATH.APP_SIGNIN)
    ) {
      cookies.save(
        COOKIES.REDIRECT_URL,
        `${location.pathname}${location.search}`,
        { path: "/" }
      );
    }
    return <Route element={<Navigate to={PATH.APP_SIGNIN} />} path="*" />;
  }

  return (
    <>
      <Route element={<AppBodyLayout />}>
        <Route path="cases">
          <Route element={<Cases />} index />
          <Route element={<Case />} path={PATH.CASE + "/*"} />
          <Route element={<EmptyBox />} path="*" />
          <Route element={<Results />} path=":id/results" />
          <Route element={<ResultsNew />} path=":id/report/results" />
          <Route element={<PanoramicView />} path=":id/panoramic" />
          <Route element={<PanoramicReport />} path=":id/panoramic-report" />
        </Route>
        <Route element={<Profile />} path="profile" />
        <Route element={<Tariffs />} path="tariffs" />
        <Route element={<ToothTestPage />} path="tooth-test-page" />
        <Route element={<SimpleTestPage />} path="simple-test-page" />
      </Route>
    </>
  );
};

function App() {
  return (
    <Suspense fallback={<Fallback />}>
      <Routes>
        <Route element={<PageLayout />} path="/">
          <Route element={<Welcome />} index />
          <Route
            element={<CephalometricAnalysis />}
            path="cephalometric-analysis"
          />
        </Route>
        <Route element={<AppLayout />} path="/app">
          <Route element={<AuthLayout />} path="auth">
            <Route element={<SignIn />} index />
            <Route element={<SignUp />} path="sign-up" />
            <Route element={<BookADemo />} path="book-a-demo" />
            <Route element={<ForgotPassword />} path="forgot-password" />
          </Route>
          <Route element={<PrivacyLayout />} path="privacy">
            <Route element={<Privacy />} path=":countryCode/:type" />
          </Route>
          <Route
            element={<PanoramicResultsView />}
            path="cases/:id/panoramic-results"
          />
          <Route element={<AppBodyLayout />}>
            <Route path="cases">
              <Route element={<Cases />} index />
              <Route element={<Case />} path={PATH.CASE + "/*"} />
              <Route element={<EmptyBox />} path="*" />
              <Route element={<Results />} path=":id/results" />
              <Route element={<ResultsNew />} path=":id/report/results" />
              <Route element={<PanoramicView />} path=":id/panoramic" />
              <Route
                element={<PanoramicReport />}
                path=":id/panoramic-report"
              />
              <Route
                element={<SpecialtySelection />}
                path=":id/specialty-selection"
              />
              <Route element={<CBCTViewer />} path=":id/cbct-viewer" />
              <Route element={<GLTFViewer />} path=":id/gltf-viewer" />
              <Route element={<Dictaphone />} path=":id/dictaphone" />
              <Route element={<DictaphoneProcessing />} path=":id/dictaphone-processing" />
              <Route element={<DictaphoneResults />} path=":id/dictaphone-results" />
            </Route>
            <Route element={<Profile />} path="profile" />
            <Route element={<Tariffs />} path="tariffs" />
            <Route element={<ToothTestPage />} path="tooth-test-page" />
            <Route element={<SimpleTestPage />} path="simple-test-page" />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
